import { useCallback } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { stompClient, useWsSubscription } from '@shared/ws';
import type { Page } from '@shared/api';
import {
  chatKeys,
  type ConversationEvent,
  type MessageStatus,
  type MessageView,
} from '@entities/chat';

interface UseConversationStreamOptions {
  conversationId: string | undefined;
  focused: boolean;
  ownUserId: string | undefined;
}

export function useConversationStream({
  conversationId,
  focused,
  ownUserId,
}: UseConversationStreamOptions) {
  const qc = useQueryClient();

  const upsertMessage = useCallback(
    (message: MessageView) => {
      if (!conversationId) return;
      qc.setQueryData<InfiniteData<Page<MessageView>>>(
        chatKeys.messages(conversationId),
        (data) => {
          if (!data) return data;
          const exists = data.pages.some((p) => p.items.some((m) => m.id === message.id));
          if (exists) {
            return {
              ...data,
              pages: data.pages.map((p) => ({
                ...p,
                items: p.items.map((m) => (m.id === message.id ? message : m)),
              })),
            };
          }
          const first = data.pages[0];
          if (!first) return data;
          const updatedFirst: Page<MessageView> = {
            ...first,
            items: [message, ...first.items],
            total: first.total + 1,
          };
          return { ...data, pages: [updatedFirst, ...data.pages.slice(1)] };
        },
      );
    },
    [conversationId, qc],
  );

  const updateStatus = useCallback(
    (messageId: string, status: MessageStatus) => {
      if (!conversationId) return;
      qc.setQueryData<InfiniteData<Page<MessageView>>>(
        chatKeys.messages(conversationId),
        (data) => {
          if (!data) return data;
          return {
            ...data,
            pages: data.pages.map((p) => ({
              ...p,
              items: p.items.map((m) => (m.id === messageId ? { ...m, status } : m)),
            })),
          };
        },
      );
    },
    [conversationId, qc],
  );

  const handleEvent = useCallback(
    (event: ConversationEvent) => {
      if (event.event === 'message.new') {
        upsertMessage(event.message);
        if (focused && ownUserId && event.message.senderId !== ownUserId && conversationId) {
          stompClient.send(`/app/conversation.${conversationId}.read`, {});
        }
      } else if (event.event === 'message.status') {
        updateStatus(event.messageId, event.status);
      }
    },
    [upsertMessage, updateStatus, focused, ownUserId, conversationId],
  );

  useWsSubscription<ConversationEvent>(
    conversationId ? `/topic/conversation.${conversationId}` : null,
    handleEvent,
  );

  const sendViaWs = useCallback(
    (body: string, attachments: string[]) => {
      if (!conversationId) return false;
      return stompClient.send(`/app/conversation.${conversationId}.send`, { body, attachments });
    },
    [conversationId],
  );

  const markReadViaWs = useCallback(() => {
    if (!conversationId) return false;
    return stompClient.send(`/app/conversation.${conversationId}.read`, {});
  }, [conversationId]);

  return { sendViaWs, markReadViaWs };
}
